import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { userApi, jobsApi, type UserStats, type JobRun, type Job } from '../lib/api';

// Progress stages for the job search
const PROGRESS_STAGES = [
  { id: 'pending', label: 'Initializing...', progress: 10 },
  { id: 'running', label: 'Scraping job boards...', progress: 40 },
  { id: 'matching', label: 'AI matching in progress...', progress: 70 },
  { id: 'completed', label: 'Search complete!', progress: 100 },
  { id: 'failed', label: 'Search failed', progress: 0 },
];

export default function Dashboard() {
  const [stats, setStats] = useState<UserStats | null>(null);
  const [recentRuns, setRecentRuns] = useState<JobRun[]>([]);
  const [topJobs, setTopJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);
  const [runLoading, setRunLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [clearingStale, setClearingStale] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Progress tracking state
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [progressStage, setProgressStage] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState<string>('');
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Check for active runs and poll for updates
  const checkActiveRuns = useCallback(async () => {
    try {
      const runsData = await jobsApi.getRuns(5);
      setRecentRuns(runsData.runs);

      // Find active run (pending or running)
      const activeRun = runsData.runs.find(
        (r) => r.status === 'pending' || r.status === 'running'
      );

      if (activeRun) {
        // Check if the run is stale (started more than 1 minute ago)
        const startedAt = new Date(activeRun.started_at).getTime();
        const now = Date.now();
        const oneMinute = 1 * 60 * 1000;

        if (now - startedAt > oneMinute) {
          // Run is stale, clear it
          console.log('Clearing stale run:', activeRun.id);
          try {
            await jobsApi.clearStaleRuns();
            // Reload runs after clearing
            const updatedRuns = await jobsApi.getRuns(5);
            setRecentRuns(updatedRuns.runs);
            setActiveRunId(null);
            setProgressStage(null);
            return;
          } catch (err) {
            console.error('Failed to clear stale runs:', err);
          }
        }
        setActiveRunId(activeRun.id);
        setProgressStage(activeRun.status);

        // Estimate progress message based on status
        if (activeRun.status === 'pending') {
          setProgressMessage('Preparing job search...');
        } else if (activeRun.status === 'running') {
          // Simulate progress through stages
          setProgressMessage('Scraping job boards and matching with AI...');
        }
      } else {
        // Check if we just completed
        if (activeRunId) {
          const justCompleted = runsData.runs.find(r => r.id === activeRunId);
          if (justCompleted?.status === 'completed') {
            setProgressStage('completed');
            setProgressMessage(`Found ${justCompleted.jobs_found} jobs, ${justCompleted.jobs_matched} matched!`);
            // Reload stats and jobs
            loadDashboard();
            // Clear after showing success
            setTimeout(() => {
              setActiveRunId(null);
              setProgressStage(null);
            }, 5000);
          } else if (justCompleted?.status === 'failed') {
            setProgressStage('failed');
            setProgressMessage(justCompleted.error_message || 'Search failed');
            setTimeout(() => {
              setActiveRunId(null);
              setProgressStage(null);
            }, 5000);
          }
        }

        // Stop polling if no active runs
        if (pollingRef.current && !activeRunId) {
          clearInterval(pollingRef.current);
          pollingRef.current = null;
        }
      }
    } catch (err) {
      console.error('Error checking runs:', err);
    }
  }, [activeRunId]);

  useEffect(() => {
    loadDashboard();
  }, []);

  // Start polling when there's an active run
  useEffect(() => {
    if (activeRunId && !pollingRef.current) {
      pollingRef.current = setInterval(checkActiveRuns, 3000);
    }

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [activeRunId, checkActiveRuns]);

  async function loadDashboard() {
    try {
      const [statsData, runsData, jobsData] = await Promise.all([
        userApi.getStats(),
        jobsApi.getRuns(5),
        jobsApi.list({ pageSize: 5, minScore: 70 }),
      ]);

      setStats(statsData);
      setRecentRuns(runsData.runs);
      setTopJobs(jobsData.jobs);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }

  async function triggerJobSearch() {
    setRunLoading(true);
    setError(null);

    try {
      const result = await jobsApi.triggerRun();
      setActiveRunId(result.runId);
      setProgressStage('pending');
      setProgressMessage('Starting job search...');

      // Start polling for updates
      checkActiveRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start job search');
    } finally {
      setRunLoading(false);
    }
  }

  async function cancelJobSearch() {
    if (!activeRunId) return;

    setCancelLoading(true);
    setError(null);

    try {
      await jobsApi.cancelRun(activeRunId);
      setProgressStage('failed');
      setProgressMessage('Search cancelled');

      // Clear after showing message
      setTimeout(() => {
        setActiveRunId(null);
        setProgressStage(null);
      }, 3000);

      // Reload runs
      checkActiveRuns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel search');
    } finally {
      setCancelLoading(false);
    }
  }

  async function clearStuckJobs() {
    setClearingStale(true);
    try {
      await jobsApi.clearStaleRuns();
      setActiveRunId(null);
      setProgressStage(null);
      await loadDashboard();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to clear stuck jobs');
    } finally {
      setClearingStale(false);
    }
  }

  // Get progress bar width based on stage
  function getProgressWidth(): string {
    const stage = PROGRESS_STAGES.find(s => s.id === progressStage);
    return stage ? `${stage.progress}%` : '0%';
  }

  // Get progress bar color based on stage
  function getProgressColor(): string {
    if (progressStage === 'failed') return 'bg-red-500';
    if (progressStage === 'completed') return 'bg-green-500';
    return 'bg-primary-500';
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner border-primary-600 border-t-transparent h-8 w-8" />
      </div>
    );
  }

  const setupComplete = stats?.setup.hasResume && stats?.setup.hasPreferences;

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <button
          onClick={triggerJobSearch}
          disabled={runLoading || !setupComplete || (progressStage !== null && progressStage !== 'completed' && progressStage !== 'failed')}
          className="btn btn-primary"
          title={!setupComplete ? 'Upload resume and set preferences first' : progressStage ? 'Search in progress...' : ''}
        >
          {runLoading ? (
            <>
              <div className="spinner mr-2" />
              Starting...
            </>
          ) : progressStage && progressStage !== 'completed' && progressStage !== 'failed' ? (
            <>
              <div className="spinner mr-2" />
              Searching...
            </>
          ) : (
            'Run Job Search'
          )}
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {/* Progress indicator */}
      {progressStage && (
        <div className={`card ${progressStage === 'failed' ? 'bg-red-50 border-red-200' : progressStage === 'completed' ? 'bg-green-50 border-green-200' : 'bg-primary-50 border-primary-200'}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              {progressStage !== 'completed' && progressStage !== 'failed' && (
                <div className="spinner border-primary-600 border-t-transparent" />
              )}
              {progressStage === 'completed' && (
                <svg className="h-6 w-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              )}
              {progressStage === 'failed' && (
                <svg className="h-6 w-6 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
              <div>
                <p className={`font-medium ${progressStage === 'failed' ? 'text-red-800' : progressStage === 'completed' ? 'text-green-800' : 'text-primary-800'}`}>
                  Job Search {progressStage === 'completed' ? 'Complete' : progressStage === 'failed' ? 'Failed' : 'In Progress'}
                </p>
                <p className={`text-sm ${progressStage === 'failed' ? 'text-red-600' : progressStage === 'completed' ? 'text-green-600' : 'text-primary-600'}`}>
                  {progressMessage}
                </p>
              </div>
            </div>
            {progressStage !== 'completed' && progressStage !== 'failed' && (
              <div className="flex gap-2">
                <button
                  onClick={cancelJobSearch}
                  disabled={cancelLoading || clearingStale}
                  className="btn btn-danger text-sm px-3 py-1"
                >
                  {cancelLoading ? 'Cancelling...' : 'Cancel'}
                </button>
                <button
                  onClick={clearStuckJobs}
                  disabled={clearingStale || cancelLoading}
                  className="btn text-sm px-3 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700"
                >
                  {clearingStale ? 'Clearing...' : 'Force Clear'}
                </button>
              </div>
            )}
            {(progressStage === 'completed' || progressStage === 'failed') && (
              <button
                onClick={() => {
                  setActiveRunId(null);
                  setProgressStage(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            )}
          </div>

          {/* Progress bar */}
          {progressStage !== 'failed' && (
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={`h-full ${getProgressColor()} transition-all duration-500 ease-out`}
                style={{ width: getProgressWidth() }}
              />
            </div>
          )}
        </div>
      )}

      {/* Setup checklist */}
      {!setupComplete && (
        <div className="card bg-yellow-50 border-yellow-200">
          <h2 className="text-lg font-semibold text-yellow-800 mb-4">Complete your setup</h2>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className={`h-6 w-6 rounded-full flex items-center justify-center ${stats?.setup.hasResume ? 'bg-green-500' : 'bg-gray-300'}`}>
                {stats?.setup.hasResume ? (
                  <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="text-white text-sm">1</span>
                )}
              </div>
              <div className="flex-1">
                <Link to="/resume" className="font-medium text-yellow-800 hover:text-yellow-900">
                  Upload your resume
                </Link>
                <p className="text-sm text-yellow-700">Your resume will be encrypted before upload</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`h-6 w-6 rounded-full flex items-center justify-center ${stats?.setup.hasPreferences ? 'bg-green-500' : 'bg-gray-300'}`}>
                {stats?.setup.hasPreferences ? (
                  <svg className="h-4 w-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <span className="text-white text-sm">2</span>
                )}
              </div>
              <div className="flex-1">
                <Link to="/preferences" className="font-medium text-yellow-800 hover:text-yellow-900">
                  Set your job preferences
                </Link>
                <p className="text-sm text-yellow-700">Tell us what kind of jobs you're looking for</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats grid */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card">
          <p className="text-sm font-medium text-gray-500">Total Jobs Found</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.jobs.total || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-500">High Matches (70%+)</p>
          <p className="mt-2 text-3xl font-bold text-green-600">{stats?.jobs.highMatches || 0}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-500">Average Match Score</p>
          <p className="mt-2 text-3xl font-bold text-primary-600">
            {stats?.jobs.averageScore ? `${stats.jobs.averageScore}%` : 'N/A'}
          </p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-gray-500">Job Searches</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{stats?.runs.total || 0}</p>
        </div>
      </div>

      {/* Two column layout */}
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
        {/* Top matches */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Top Matches</h2>
            <Link to="/jobs" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          {topJobs.length === 0 ? (
            <p className="text-gray-500 text-sm">No job matches yet. Run a search to find matching jobs.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {topJobs.map((job) => (
                <li key={job.id} className="py-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <a
                        href={job.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-gray-900 hover:text-primary-600 truncate block"
                      >
                        {job.title || 'Untitled'}
                      </a>
                      <p className="text-sm text-gray-500 truncate">
                        {job.company || 'Unknown Company'}
                        {job.location && ` • ${job.location}`}
                      </p>
                    </div>
                    <span className={`badge ml-2 ${
                      (job.match_score || 0) >= 80 ? 'badge-success' : 'badge-info'
                    }`}>
                      {job.match_score}%
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Recent runs */}
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Recent Searches</h2>
            {recentRuns.some(r => r.status === 'pending' || r.status === 'running') && (
              <button
                onClick={clearStuckJobs}
                disabled={clearingStale}
                className="btn btn-danger text-sm px-3 py-1"
              >
                {clearingStale ? 'Clearing...' : 'Clear Stuck Jobs'}
              </button>
            )}
          </div>
          {recentRuns.length === 0 ? (
            <p className="text-gray-500 text-sm">No searches yet. Click "Run Job Search" to start.</p>
          ) : (
            <ul className="divide-y divide-gray-200">
              {recentRuns.map((run) => (
                <li key={run.id} className="py-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-900">
                        {new Date(run.started_at).toLocaleDateString()} at{' '}
                        {new Date(run.started_at).toLocaleTimeString()}
                      </p>
                      <p className="text-sm text-gray-500">
                        {run.status === 'completed'
                          ? `Found ${run.jobs_found} jobs, ${run.jobs_matched} matched`
                          : run.status === 'failed'
                          ? run.error_message || 'Search failed'
                          : 'In progress...'}
                      </p>
                    </div>
                    <span className={`badge ${
                      run.status === 'completed'
                        ? 'badge-success'
                        : run.status === 'failed'
                        ? 'badge-error'
                        : 'badge-warning'
                    }`}>
                      {run.status}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Version footer for debugging */}
      <div className="text-center text-xs text-gray-400 mt-8">
        Frontend v1.2.0 - Cancel & Clear Update
      </div>
    </div>
  );
}
